import { FC } from 'react'
import { Box, Button, makeStyles, Typography } from '@perk-ui/core'

import BrandLogo from '../../../assets/BrandLogo.png'

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #182C5A 0%, #324395 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  container: {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: theme.spacing(4),
    display: 'flex',
    alignItems: 'center',
    minHeight: '600px',
  },
  leftPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(6),
    color: '#FFFFFF',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(3),
      textAlign: 'center',
    },
  },
  logo: {
    marginBottom: theme.spacing(4),
    '& img': {
      height: '40px',
      width: 'auto',
      filter: 'brightness(0) invert(1)',
    },
  },
  title: {
    fontSize: '48px',
    fontWeight: 700,
    lineHeight: '1.2',
    marginBottom: theme.spacing(3),
    color: '#FFFFFF',
    [theme.breakpoints.down('sm')]: {
      fontSize: '36px',
    },
  },
  subtitle: {
    fontSize: '18px',
    fontWeight: 400,
    color: 'rgba(255, 255, 255, 0.8)',
    lineHeight: '1.6',
    marginBottom: theme.spacing(4),
    maxWidth: '500px',
  },
  button: {
    backgroundColor: '#2E8099',
    color: '#FFFFFF',
    padding: theme.spacing(1.5, 4),
    fontSize: '16px',
    fontWeight: 600,
    borderRadius: '8px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#215B6D',
    },
  },
  rightPanel: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  dashboardPreview: {
    width: '100%',
    maxWidth: '500px',
    height: '350px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '16px',
    padding: theme.spacing(3),
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    display: 'flex',
    flexDirection: 'column',
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: '1px solid #E5E7EB',
  },
  previewLogo: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1B365D',
  },
  previewTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#212121',
    marginBottom: theme.spacing(2),
  },
  previewStats: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  statCard: {
    flex: 1,
    padding: theme.spacing(1.5),
    backgroundColor: '#F8F9FA',
    borderRadius: '8px',
    textAlign: 'center',
  },
  statNumber: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#1B365D',
    lineHeight: 1,
  },
  statLabel: {
    fontSize: '12px',
    color: '#6B7280',
    marginTop: theme.spacing(0.5),
  },
  chartArea: {
    flex: 1,
    backgroundColor: '#F8F9FA',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  chartBars: {
    display: 'flex',
    alignItems: 'end',
    gap: '4px',
    height: '80px',
  },
  chartBar: {
    width: '12px',
    backgroundColor: '#4A7C83',
    borderRadius: '2px 2px 0 0',
  },
}))

interface NoTokenPageProps {
  reason?: 'missing' | 'invalid' | 'expired'
}

const NoTokenPage: FC<NoTokenPageProps> = ({ reason: _reason = 'invalid' }) => {
  const classes = useStyles()

  const handleGoHome = () => {
    // Redirect to company website or contact page instead of login
    window.location.href = 'https://www.databiologics.com'
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.container}>
        <Box className={classes.leftPanel}>
          <Box className={classes.logo}>
            <img src={BrandLogo} alt="DataBiologics" />
          </Box>

          <Typography className={classes.title}>
            This invite link has expired or is no longer valid.
          </Typography>

          <Typography className={classes.subtitle}>
            This invitation may have been used already, expired due to time
            limits, or the link may be malformed. Please contact your sponsor or
            the person who sent you this invitation to request a new link.
          </Typography>

          <Button
            variant="contained"
            className={classes.button}
            onClick={handleGoHome}
          >
            Visit DataBiologics.com
          </Button>
        </Box>

        <Box className={classes.rightPanel}>
          <Box className={classes.dashboardPreview}>
            <Box className={classes.previewHeader}>
              <Box className={classes.previewLogo}>
                <img
                  src={BrandLogo}
                  alt="DataBiologics"
                  style={{ height: '16px', width: 'auto' }}
                />
              </Box>
              <Box style={{ display: 'flex', gap: '8px' }}>
                <Box
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#EF4444',
                  }}
                />
                <Box
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#F59E0B',
                  }}
                />
                <Box
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#10B981',
                  }}
                />
              </Box>
            </Box>

            <Typography className={classes.previewTitle}>
              Welcome back
            </Typography>

            <Box className={classes.previewStats}>
              <Box className={classes.statCard}>
                <Typography className={classes.statNumber}>326</Typography>
                <Typography className={classes.statLabel}>Patients</Typography>
              </Box>
              <Box className={classes.statCard}>
                <Typography className={classes.statNumber}>484</Typography>
                <Typography className={classes.statLabel}>Cases</Typography>
              </Box>
              <Box className={classes.statCard}>
                <Typography className={classes.statNumber}>90%</Typography>
                <Typography className={classes.statLabel}>
                  Success Rate
                </Typography>
              </Box>
              <Box className={classes.statCard}>
                <Typography className={classes.statNumber}>88%</Typography>
                <Typography className={classes.statLabel}>
                  Satisfaction
                </Typography>
              </Box>
            </Box>

            <Box className={classes.chartArea}>
              <Box className={classes.chartBars}>
                <Box className={classes.chartBar} style={{ height: '30%' }} />
                <Box className={classes.chartBar} style={{ height: '45%' }} />
                <Box className={classes.chartBar} style={{ height: '35%' }} />
                <Box className={classes.chartBar} style={{ height: '60%' }} />
                <Box className={classes.chartBar} style={{ height: '50%' }} />
                <Box className={classes.chartBar} style={{ height: '75%' }} />
                <Box className={classes.chartBar} style={{ height: '80%' }} />
                <Box className={classes.chartBar} style={{ height: '65%' }} />
                <Box className={classes.chartBar} style={{ height: '85%' }} />
                <Box className={classes.chartBar} style={{ height: '70%' }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default NoTokenPage
